import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { MongoService }  from '../mongo_support';
import { Router, NavigationEnd } from '@angular/router';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import imageCompression from 'browser-image-compression';

import { NavigatorService, keyTypesEnum } from './navigator.service';

// AWS
import * as AWS from 'aws-sdk/global';
import * as S3 from 'aws-sdk/clients/s3';
import { el } from 'date-fns/locale';
import { LinkedAccount } from 'src/app/data-models/models';
import { PlatformService } from './platform.service';


@Injectable({
    providedIn: 'root'
})

export class ImageHandlerService {
    private returnedImageSubject = new Subject<any>();
    private returnedImagesSubject = new Subject<any>();

    private accessKeyId:string = environment?.awsS3AccessKey;
    private secretAccessKey:string = environment?.awsS3SecretKey;

    private region:string = 'us-east-2';

    private keyTypes:any = keyTypesEnum;
    private returnedFile: any = null;


    constructor(
        public http:HttpClient, 
        public realm:MongoService, 
        public router:Router, 
        private navigatorService: NavigatorService, 
        private platformService: PlatformService,
        private sanitizer: DomSanitizer) { 

    }


    async uploadFile(file: File, fileId: string, folder?: string): Promise<void> {
        await this.compressImage(file).then(async compressedImg => {
            if(compressedImg) {

                if(folder == undefined || folder == null || folder.length == 0) {
                    folder = this.navigatorService.getCompanyId();

                    if(folder == undefined || folder == null || folder.length == 0) {
                        folder = this.navigatorService.getProfileId();
                    }
                }

                await this.uploadToServer(compressedImg, fileId, folder);
            }
        });

        console.log("Image Upload Return");
    }

    private async compressImage(file: File): Promise<File> {

        if(!file.type.includes('image')) {
            return file;
        }

        const imageFile = file;
        console.log('originalFile instanceof Blob', imageFile instanceof Blob); // true
        console.log(`originalFile size ${imageFile.size / 1024 / 1024} MB`);
      
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        }

        try {
          const compressedFile = await imageCompression(imageFile, options);
          console.log('compressedFile instanceof Blob', compressedFile instanceof Blob); // true
          console.log(`compressedFile size ${compressedFile.size / 1024 / 1024} MB`); // smaller than maxSizeMB

          return compressedFile

        } catch (error) {
          console.log(error);
        }
      
    }

    private async fileToBase64(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file); // reads as base64
          reader.onload = () => {
            const result = reader.result as string;

            // Strip the prefix "data:image/jpeg;base64," or similar
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = error => reject(error);
        });
      }
      

    private async uploadToServer(file: File, fileId: string, folder?: string): Promise<boolean> {

        if(folder == undefined || folder.length == 0) {
            folder = this.navigatorService.getProfileId();
        }

        const contentType = file.type;

        const success: boolean = await this.s3UploadFile(file, fileId, folder);

        return success;
    }


    async getAsyncFiles(fileIds: Array<string> | Array<LinkedAccount>, folder?: string, isLinkedAccounts?: boolean): Promise<Array<SafeUrl>> {
        let files: Array<SafeUrl> = [];

        let folderID: string = null;
        
        if(folder == undefined || folder == null || folder.length <= 0) {
            folderID = this.navigatorService.getCompanyId();
        } else {
            folderID = folder;
        }

        if(folderID == undefined || folderID == null || folderID.length == 0) {
            folderID = this.navigatorService.getProfileId();
        }
        
        await this.getFilesFromServer(fileIds, [], folderID, isLinkedAccounts).then(data => {

            if(data != null && data != undefined) {
                files = data;
            }

        });

        console.log("Async Images: ", files);

        return files;
    }

    async getAsyncFile(fileId: string): Promise<SafeUrl> {
        let file: SafeUrl = '';

        let folderID: string = this.navigatorService.getCompanyId();

        if(folderID == undefined || folderID == null || folderID.length == 0) {
            folderID = this.navigatorService.getProfileId();
        }
        
        await this.getAsyncFileFromServer(fileId, folderID).then(data => {

            if(data != null && data != undefined) {
                file = data;
            }

        });

        console.log("Async Images: ", file);

        return file;
    }

    getFiles(fileIds: Array<string>): Observable<any> {
        let folderID: string = this.navigatorService.getCompanyId();

        if(folderID == undefined || folderID == null || folderID.length == 0) {
            folderID = this.navigatorService.getProfileId();
        }

        this.getFilesFromServer(fileIds, [], folderID);

        return this.returnedImagesSubject.asObservable();
    }

    getFile(fileId: string): Observable<any> {
        let folderID: string = this.navigatorService.getCompanyId();

        if(folderID == undefined || folderID == null || folderID.length == 0) {
            folderID = this.navigatorService.getProfileId();
        }

        this.getFileFromServer(fileId, folderID);

        return this.returnedImageSubject.asObservable();
    }

    private async getFileFromServer(fileId: string, companyId?: string): Promise<void> {
        let folder = this.navigatorService.getCompanyId();

        if(companyId != undefined) {
            folder = companyId;
        }

        let presignedURL: string | null = await this.getPresignedURL(fileId, folder);
        this.returnedImageSubject.next(presignedURL);
    }

    private async getAsyncFileFromServer(fileId: string, companyId?: string): Promise<SafeUrl> {

        let presignedURL: string | null = await this.getPresignedURL(fileId, companyId);

        if (!presignedURL) 
            presignedURL = "Failed";

        return presignedURL;
        
    }

    private async getFilesFromServer(fileIds: Array<any>, returnedFiles: Array<SafeUrl>, companyId?: string, isLinkedAccounts?: boolean): Promise<Array<SafeUrl>> {

        let folder = this.navigatorService.getCompanyId();

        if(companyId != undefined) {
            folder = companyId;
        }

        for(let fileId of fileIds) {

            if(isLinkedAccounts) {
                folder = fileId.id;
                fileId = fileId.profileImg;
            }

            if(fileId != null && fileId.length > 0 && fileId != "Empty Image ID") {

                if(isLinkedAccounts) {
                    console.log("Obtaining Linked Account Folder: ", folder);
                    console.log("Obtaining Linked Account ID: ", fileId);
                }

                try {

                    let presignedURL: string | null = await this.getPresignedURL(fileId, folder);

                    if (!presignedURL) 
                        presignedURL = "Failed";

                    returnedFiles.push(presignedURL);

                } catch (error) {
                    console.log("Failed to pull image: ", error);
                    returnedFiles.push("Failed");
                }
            } else {
                returnedFiles.push("Failed");
            }

        }

        return returnedFiles;

    }

    private convertToImageBlob(image) {
        
        return URL.createObjectURL( new Blob([image.Body], {type: image.ContentType } ));

        

        // if(image != undefined && image != null && image.Body) {
        //     return URL.createObjectURL( new Blob([image.Body], {type: image.ContentType } ));
        // } else {
        //     return "";
        // }
    }

    private sanitizeImageURL(imageURL:string): SafeUrl {
        let sanitizedUrl = this.sanitizer.bypassSecurityTrustUrl(imageURL);

        return sanitizedUrl;
    }

    public async getPresignedURL(fileId: string, folderId: string = this.navigatorService.getCompanyId()): Promise<string | null> {

        const token: string | null = this.platformService.getAuthToken();
        let url: string | null = null;

        console.log("Presigned Image URL Params: ", { token: token, fileId: fileId, folderId: folderId })

        await this.http.post(`${environment.hostURL}/platform/api/generatePresignedUrl`, 
        { token: token, fileId: fileId, folderId: folderId } ).toPromise().then( 
            (data: string
                ) => {
                    console.log("Img Response", data );
                    url = data;
                }
        );

        return url;
    }

    public async s3UploadFile(file: File, fileId: string, folderId: string = this.navigatorService.getCompanyId()): Promise<boolean> {
        const token: string | null = this.platformService.getAuthToken();
        let success: boolean = false;
      
        const base64String = await this.fileToBase64(file);
      
        console.log("Uploading Base64 Image: ", { fileId, folderId });
      
        await this.http.post(`${environment.hostURL}/platform/api/s3/uploadFile`, {
          token,
          file: base64String,
          fileId,
          folderId,
          isBase64: true
        }).toPromise().then((data: { message: string, success: boolean }) => {
          success = data.success;
        });
      
        return success;
    }
      
}